import { InputField, SelectSimple } from '@advanza/input'
import { SelectSimpleChangeEvent } from '@advanza/input/interfaces/SelectSimpleChangeEvent'
import { Button, Icon } from '@advanza/ui'
import { TrustCheckCountry } from '@trustoo/types/api/controllers/trustCheckReport'
import { COUNTRIES } from 'components/reports/trustCheck/trustCheckLabels'
import {
    fetchTrustCheckReport,
    fetchTrustCheckRuns,
    fetchTrustCheckSummary,
    startTrustCheckRun,
} from 'features/reports/trustCheckReportSlice'
import { useAppDispatch, useAppSelector } from 'hooks/hooks'
import { hasAccess } from 'misc/user'
import { useEffect, useRef, useState } from 'react'

const POLL_INTERVAL_MS = 5000
const DEFAULT_MONTHS = 12

/**
 * Shows the run behind the current results with its progress, and lets an office user with export
 * write access start a new dry run. Polls while a run is pending or running and reloads the report
 * when it finishes.
 */
const TrustCheckRunSelector = () => {
    const dispatch = useAppDispatch()
    const { runs, run, isStarting, startError, filters, sorting, pagination } = useAppSelector(
        (state) => state.trustCheckReport,
    )
    const [country, setCountry] = useState<TrustCheckCountry | ''>('')
    const [months, setMonths] = useState(DEFAULT_MONTHS)
    const canStart = hasAccess('export', 'w')

    const activeRun = runs.find((item) => item.status === 'pending' || item.status === 'running')
    const activeRunKey = activeRun
        ? `${activeRun.trust_check_run_id}:${activeRun.status}:${activeRun.phase}:${activeRun.processed_count}:${activeRun.website_pending_count}`
        : null

    useEffect(() => {
        dispatch(fetchTrustCheckRuns())
    }, [dispatch])

    useEffect(() => {
        if (activeRunKey === null) {
            return undefined
        }
        const timer = window.setTimeout(() => dispatch(fetchTrustCheckRuns()), POLL_INTERVAL_MS)
        return () => window.clearTimeout(timer)
    }, [dispatch, activeRunKey])

    // When the active run finishes, reload: without an explicit run filter its results become the report.
    const hadActiveRun = useRef(false)
    useEffect(() => {
        if (activeRun !== undefined) {
            hadActiveRun.current = true
            return
        }
        if (hadActiveRun.current) {
            hadActiveRun.current = false
            dispatch(fetchTrustCheckReport({ filters, page: pagination?.page ?? 0, sorting }))
            dispatch(fetchTrustCheckSummary({ runId: filters.run_id, country: filters.country }))
        }
    }, [dispatch, activeRun, filters, sorting, pagination])

    const handleStart = async () => {
        const result = await dispatch(startTrustCheckRun({ country, months }))
        if (startTrustCheckRun.fulfilled.match(result)) {
            dispatch(fetchTrustCheckRuns())
        }
    }

    const renderProgress = () => {
        if (!activeRun) {
            return null
        }
        const percentage =
            activeRun.population_size > 0
                ? Math.round((activeRun.processed_count / activeRun.population_size) * 100)
                : 0
        const label = `Run #${activeRun.trust_check_run_id} (${activeRun.country_code ?? 'all markets'})`

        if (activeRun.phase === 'warming_up_websites') {
            return (
                <div className="flex items-center gap-2 text-md text-grey-800">
                    <Icon name="hourglass_top" fontSize={16} classNameIcon="text-primary" />
                    <span>
                        {label} is waiting for the Supply Agent to crawl the provider websites:{' '}
                        {activeRun.website_pending_count ?? '…'} of {activeRun.population_size}{' '}
                        still pending. Evaluation starts when they are done or the wait runs out.
                    </span>
                </div>
            )
        }

        return (
            <div className="flex items-center gap-2 text-md text-grey-800">
                <Icon name="hourglass_top" fontSize={16} classNameIcon="text-primary" />
                <span>
                    {label} is {activeRun.status}: {activeRun.processed_count} /{' '}
                    {activeRun.population_size} providers ({percentage}%)
                </span>
            </div>
        )
    }

    const renderCurrentRun = () => {
        if (!run) {
            return (
                <span className="text-md text-grey-700">
                    No completed run yet. Start one to populate the report.
                </span>
            )
        }

        return (
            <span className="text-md text-grey-800">
                Showing run <strong>#{run.trust_check_run_id}</strong> ·{' '}
                {run.country_code ?? 'all markets'} · Premium since {run.population_since} ·{' '}
                {run.population_size} providers · finished {run.finished_at?.slice(0, 16) ?? '-'}
                {run.website_pending_count !== null && run.website_pending_count > 0 && (
                    <span className="ml-2 text-paused-orange">
                        {run.website_pending_count} websites were still being crawled; their website
                        check is &quot;crawl pending&quot;. Run again later for those.
                    </span>
                )}
                {run.status === 'failed' && run.error_message !== null && (
                    <span className="ml-2 text-error">Failed: {run.error_message}</span>
                )}
            </span>
        )
    }

    return (
        <div className="mb-4 flex flex-wrap items-end justify-between gap-4 rounded-lg border border-grey-200 bg-white p-4">
            <div className="flex flex-col gap-2">
                {renderCurrentRun()}
                {renderProgress()}
                {startError !== null && <span className="text-md text-error">{startError}</span>}
            </div>
            {canStart && (
                <div className="flex flex-wrap items-end gap-3">
                    <div className="w-[170px]">
                        <SelectSimple
                            placeholder="Market"
                            value={country}
                            onChange={(e: SelectSimpleChangeEvent) =>
                                setCountry(
                                    e.target.value === null
                                        ? ''
                                        : (String(e.target.value) as TrustCheckCountry),
                                )
                            }
                            options={[
                                { value: '', name: 'All markets' },
                                ...COUNTRIES.map((code) => ({ value: code, name: code })),
                            ]}
                        />
                    </div>
                    <div className="w-[130px]">
                        <InputField
                            type="number"
                            min={1}
                            max={60}
                            placeholder="Months"
                            value={months}
                            onChange={(e) => setMonths(Math.max(1, Number(e.target.value) || 1))}
                        />
                    </div>
                    <Button
                        variant="primary"
                        disabled={isStarting || activeRun !== undefined}
                        onClick={handleStart}
                        before={<Icon name="play_arrow" />}>
                        {isStarting ? 'Starting…' : 'Start new run'}
                    </Button>
                </div>
            )}
        </div>
    )
}

export default TrustCheckRunSelector
